/**
 * SharedConversationPage - Public view for shared content.
 * Routes to appropriate renderer based on object_type.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { SLIDEOUT_TIMELINE_ID_PARAM } from '@/lib/crm/contexts/SlideoutStackContext';
import { entityTypeToRouteSlug } from '@/lib/crm/utils/entityTypeUtils';
import {
  createEntityMarkdownComponents,
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entityRendering';
import { shareService } from '@/lib/share/service';
import type { SharedContent } from '@/types/share';

import { SharedDynamicViewPage } from './SharedDynamicViewPage';
import { SharedDashboardPage, SharedReportPage } from './SharedReportPage';
import { ShareErrorPage } from './ShareErrorPage';

export function SharedConversationPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await shareService.resolveShareLink(token);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof Error && err.message.includes('403')) {
            setError('You do not have permission to view this content');
          } else if (err instanceof Error && err.message.includes('404')) {
            setError('This link is no longer available');
          } else {
            setError('Unable to load shared content');
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-full bg-bg-primary flex flex-col'>
        <header className='border-b border-border-secondary px-6 py-4'>
          <a href='https://ahoy.ai'>
            <img
              src='/ahoy-logo-with-text-black.svg'
              alt='Ahoy'
              className='h-6 dark:invert'
            />
          </a>
        </header>
        <main className='flex-1 flex items-center justify-center'>
          <LoadingIndicator size='sm' label='Loading…' />
        </main>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return <ShareErrorPage message={error || 'Link not found'} />;
  }

  // Route to appropriate renderer based on object_type
  if (data.object_type === 'dynamic_view') {
    return <SharedDynamicViewPage data={data.data} />;
  }
  if (data.object_type === 'report') {
    return <SharedReportPage data={data.data} />;
  }
  if (data.object_type === 'dashboard') {
    return <SharedDashboardPage data={data.data} />;
  }

  // Default: conversation content
  const conversationData = data.data;
  const orgId = conversationData.org_id ?? null;
  const linkBuilder =
    orgId != null
      ? (id: string, type: string) =>
          `/${orgId}/crm/${entityTypeToRouteSlug(type)}?${SLIDEOUT_TIMELINE_ID_PARAM}=${encodeURIComponent(id)}`
      : undefined;
  const entityComponents = createEntityMarkdownComponents(
    undefined,
    linkBuilder
  );

  return (
    <div className='min-h-full bg-bg-primary flex flex-col'>
      {/* Header with logo */}
      <header className='border-b border-border-secondary px-6 py-4'>
        <a href='https://ahoy.ai'>
          <img
            src='/ahoy-logo-with-text-black.svg'
            alt='Ahoy'
            className='h-6 dark:invert'
          />
        </a>
      </header>

      {/* Shared conversation content */}
      <main className='flex-1 max-w-4xl mx-auto w-full px-6 py-8'>
        {/* Conversation title */}
        <h1 className='text-2xl font-semibold text-fg-primary mb-2'>
          {conversationData.title || 'Shared Conversation'}
        </h1>
        {conversationData.description && (
          <p className='text-fg-secondary mb-6'>
            {conversationData.description}
          </p>
        )}

        {/* Conversation chains */}
        <div className='space-y-8'>
          {conversationData.chains.map(chain => (
            <div key={chain.id} className='space-y-4'>
              {/* User message */}
              <div className='flex justify-end'>
                <div className='max-w-[85%]'>
                  <p className='text-xs text-fg-tertiary mb-1.5 text-right'>
                    User
                  </p>
                  <div className='bg-bg-secondary border border-border-secondary rounded-bl-lg rounded-br-lg rounded-tl-lg px-4 py-3'>
                    <p className='text-fg-primary whitespace-pre-wrap break-words'>
                      {chain.user_message}
                    </p>
                  </div>
                </div>
              </div>

              {/* AI responses */}
              {chain.steps.map(step => (
                <div key={step.step_number} className='space-y-2'>
                  {step.is_final && (
                    <div className='prose prose-sm max-w-none text-fg-primary'>
                      <MarkdownContent
                        content={preprocessEntityTags(step.content)}
                        rehypePlugins={[
                          rehypeRaw,
                          [rehypeSanitize, entitySanitizeSchema],
                        ]}
                        components={entityComponents}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Dynamic views preview (if any) */}
        {conversationData.dynamic_views &&
          conversationData.dynamic_views.length > 0 && (
            <div className='mt-8 pt-8 border-t border-border-secondary'>
              <h2 className='text-lg font-semibold text-fg-primary mb-4'>
                Generated views
              </h2>
              <div className='grid gap-4 sm:grid-cols-2'>
                {conversationData.dynamic_views.map(view => (
                  <div
                    key={view.id}
                    className='p-4 bg-bg-secondary border border-border-secondary rounded-lg'
                  >
                    <h3 className='font-medium text-fg-primary'>
                      {view.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}
      </main>

      {/* Footer with Ahoy branding */}
      <footer className='border-t border-border-secondary px-6 py-4 text-center'>
        <p className='text-sm text-fg-tertiary'>
          Shared via{' '}
          <a
            href='https://ahoy.ai'
            className='text-brand-primary hover:underline'
          >
            Ahoy
          </a>
        </p>
      </footer>
    </div>
  );
}
